import { type ReportChatMessage } from './interfaces';

/**
 * Pure read-state derivations shared by the thread (divider) and the host (unread
 * dot). All state is derived from two backend-owned facts — the ordered messages
 * and the read cursor (highest message id the viewer has read) — so the client
 * never guesses: no optimistic flips, no races to reconcile. `cursor` is `null`
 * when nothing has been read yet.
 *
 * "Real" messages are numeric-id rows delivered by the socket; synthesized
 * report-prefix rows carry string ids and are ignored (they are the student's own
 * report body, never an unread boundary).
 */

function isRealId(message: ReportChatMessage): message is ReportChatMessage & { id: number } {
  return typeof message.id === 'number';
}

/**
 * The first message past the read cursor authored by the OTHER side (not the
 * viewer) — the "new messages" divider anchor. The viewer's own messages are
 * never unread to them, so they never anchor the divider. System status notices
 * («Обращение решено», «Ждём вашего ответа») are not unread either: the server
 * counts only replies, and the thread's read tracking walks message bubbles only,
 * so a trailing notice would stay "unread" forever. `null` when everything is read.
 */
export function firstUnreadMessageId(messages: ReportChatMessage[], cursor: number | null): number | null {
  for (const message of messages) {
    if (
      isRealId(message) &&
      !message.mine &&
      message.authorRole !== 'system' &&
      (cursor === null || message.id > cursor)
    ) {
      return message.id;
    }
  }
  return null;
}

/**
 * Whether the thread holds a reply past the cursor from the other side — i.e. the
 * report carries an unread reply. Drives the unread dot; for the student it matches
 * the server's `reports_with_unread` (staff replies only, system notices excluded),
 * so the dot follows the real cursor, not an optimistic flip.
 */
export function hasUnreadMessages(messages: ReportChatMessage[], cursor: number | null): boolean {
  return firstUnreadMessageId(messages, cursor) !== null;
}

/** Highest real (numeric) message id in the thread, or `null` when there is none. */
export function latestMessageId(messages: ReportChatMessage[]): number | null {
  let max: number | null = null;
  for (const message of messages) {
    if (isRealId(message) && (max === null || message.id > max)) {
      max = message.id;
    }
  }
  return max;
}
